import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';

export default function PrivacyPolicy() {
  return (
    <Layout>
      <SEO
        title="Privacy Policy - B2B CSV"
        description="B2B CSV Privacy Policy. Learn how we collect, use, and protect your personal information and business data."
        canonical="/privacy"
      />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold text-slate-100 mb-4">Privacy Policy</h1>
        <p className="text-slate-400 mb-8">Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

        <div className="space-y-8 text-slate-300">
          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">1. Introduction</h2>
            <p>
              Welcome to B2B CSV ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our business search and lead generation service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">2. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">2.1 Information You Provide</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Account Information:</strong> Name, email address, company name, and password when you create an account</li>
              <li><strong>Payment Information:</strong> Billing information processed securely through Stripe (we do not store full credit card numbers)</li>
              <li><strong>Search Queries:</strong> Business search terms and filters you input into our service</li>
              <li><strong>Saved Searches:</strong> Search results and business data you choose to save</li>
              <li><strong>Communications:</strong> Information in messages you send to us (support requests, feedback)</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">2.2 Information Collected Automatically</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Usage Data:</strong> Search history, features used, time spent, and interaction patterns</li>
              <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers</li>
              <li><strong>Cookies:</strong> Authentication tokens and session information (see Cookie Policy below)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">3. How We Use Your Information</h2>
            <p className="mb-3">We use the information we collect to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide, operate, and maintain our service</li>
              <li>Process your search requests using Google Gemini AI and Google Places API</li>
              <li>Manage your account and subscription</li>
              <li>Process payments through Stripe</li>
              <li>Send you service-related notifications and updates</li>
              <li>Respond to your support requests and feedback</li>
              <li>Monitor usage to prevent abuse and ensure fair use</li>
              <li>Analyze usage patterns to improve our service</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">4. How We Share Your Information</h2>
            <p className="mb-3">We share your information only in the following circumstances:</p>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">4.1 Service Providers</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Google (Gemini AI & Places API):</strong> To process your search queries and retrieve business data</li>
              <li><strong>Stripe:</strong> To process payments securely</li>
              <li><strong>Railway:</strong> Cloud hosting infrastructure</li>
              <li><strong>Email Service Providers:</strong> To send verification and notification emails</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">4.2 Legal Requirements</h3>
            <p>We may disclose your information if required by law, court order, or governmental request, or to protect our rights and safety.</p>

            <h3 className="text-xl font-semibold text-slate-200 mb-2 mt-4">4.3 Business Transfers</h3>
            <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new entity.</p>

            <p className="mt-4 font-semibold">We do NOT sell your personal information to third parties.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">5. Data Security</h2>
            <p className="mb-3">We implement industry-standard security measures to protect your information:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Encrypted data transmission using HTTPS/SSL</li>
              <li>Password hashing using bcrypt</li>
              <li>Secure authentication with HTTP-only cookies</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and monitoring</li>
            </ul>
            <p className="mt-3">
              However, no method of transmission over the internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">6. Data Retention</h2>
            <p>We retain your information for as long as your account is active or as needed to provide services. You may delete your account at any time, after which we will delete or anonymize your personal information within 30 days, except where we are required to retain it for legal purposes.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">7. Your Privacy Rights</h2>
            <p className="mb-3">Depending on your location, you may have the following rights:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of your personal information</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Portability:</strong> Export your saved searches in CSV format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing emails</li>
              <li><strong>Restrict Processing:</strong> Limit how we use your information</li>
            </ul>
            <p className="mt-3">To exercise these rights, please contact us at the email address below.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">8. Cookies and Tracking</h2>
            <p className="mb-3">We use essential cookies for:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Authentication and session management</li>
              <li>Security and fraud prevention</li>
              <li>Remembering your preferences</li>
            </ul>
            <p className="mt-3">
              You can control cookies through your browser settings. Note that disabling cookies may affect service functionality.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">9. Third-Party Services</h2>
            <p className="mb-3">Our service integrates with third-party services that have their own privacy policies:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Google:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Google Privacy Policy</a></li>
              <li><strong>Stripe:</strong> <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Stripe Privacy Policy</a></li>
            </ul>
            <p className="mt-3">We are not responsible for the privacy practices of these third-party services.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">10. Children's Privacy</h2>
            <p>Our service is not intended for children under 13 years of age. We do not knowingly collect information from children. If you believe we have collected information from a child, please contact us immediately.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">11. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in the United States or other countries where our service providers operate. By using our service, you consent to these transfers. We ensure appropriate safeguards are in place for international transfers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">12. California Privacy Rights (CCPA)</h2>
            <p className="mb-3">If you are a California resident, you have additional rights under the California Consumer Privacy Act:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or shared (we do not sell personal information)</li>
              <li>Right to opt-out of the sale of personal information</li>
              <li>Right to deletion of personal information</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">13. GDPR Rights (European Users)</h2>
            <p className="mb-3">If you are in the European Economic Area, you have rights under the General Data Protection Regulation (GDPR):</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Right to access your personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Right to withdraw consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">14. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of the service after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">15. Contact Us</h2>
            <p className="mb-3">If you have questions or concerns about this Privacy Policy, please contact us:</p>
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <p><strong>Email:</strong> privacy@b2bcsv.com</p>
              <p><strong>Support:</strong> support@b2bcsv.com</p>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}
